import {createStyles} from '@mantine/core';
import {css} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ANALYTICS_MODULE, ACTION_CATEGORY} from 'analytics/AnalyticsTypes';
import {Section} from 'constants/NavConstants';
import {isNavbarColored, makeNavbarBackgroundStyles} from 'nav/navbar/NavBarColor';
import {NavBarIconProps} from 'nav/navbar/NavBarIcon';
import {getTabStyles, navBarIconStyles} from 'nav/navbar/NavBarStyles';
import {sectionToReadable} from 'nav/NavUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import Float from 'ui-library/floating-ui/Float';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const useStyles = createStyles((theme) => {
  const {backgroundColor = theme.colors.white} = makeNavbarBackgroundStyles();
  return {
    menu: {
      backgroundColor: `${theme.colors.blue[8]} !important`,
    },
    coloredMenu: {
      // Our Menu helpfully sets backgroundColor to white. Thankfully this is done in LESS
      backgroundColor: `${backgroundColor} !important`,
    },
    impersonatingMenu: {
      backgroundColor: `${theme.colors.red[6]} !important`,
    },
    menuItem: {
      backgroundColor: `${theme.colors.blue[8]} !important`,
      color: `${theme.colors.gray[2]} !important`,
      '&:hover, &:active, &:focus': {
        backgroundColor: `${theme.colors.blue[9]} !important`,
        color: `${theme.white} !important`,
      },
    },
    coloredMenuItem: {
      backgroundColor: `${backgroundColor} !important`,
      color: `${theme.colors.gray[2]} !important`,
      '&:hover, &:active, &:focus': {
        backgroundColor: 'rgba(0,0,0,0.1) !important',
        color: `${theme.white} !important`,
      },
    },
    impersonatingMenuItem: {
      backgroundColor: `${theme.colors.red[6]} !important`,
      color: `${theme.colors.gray[2]} !important`,
      '&:hover, &:active, &:focus': {
        backgroundColor: `${theme.colors.red[9]} !important`,
        color: `${theme.white} !important`,
      },
    },
  };
});

export interface NavBarOverflowMenuProps {
  activeSection: Section | null;
  isOverflowMenuOpen: boolean;
  sections: Array<Pick<NavBarIconProps, 'className' | 'icon' | 'isActive' | 'onSectionClick' | 'section'>>;
}

export default function NavBarOverflowMenu({
  activeSection,
  isOverflowMenuOpen,
  sections,
}: NavBarOverflowMenuProps): JSX.Element {
  const {classes, cx} = useStyles();
  const isActive = isOverflowMenuOpen || sections.some(({section}) => section === activeSection);
  const isColoredNavbar = isNavbarColored();
  const isImpersonating = window.impersonating;
  return (
    <div className={css(getTabStyles(isActive))}>
      <Float
        interaction={FloatingInteraction.CONTROLLED}
        placement={FloatingPlacement.RIGHT_END}
        isOpen={isOverflowMenuOpen}
        role="dialog"
        onRequestClose={() => {
          if (isOverflowMenuOpen) {
            SidepanelActions.closeOverflowMenu();
          }
        }}
        floatingElementOptions={{
          dataTestAttrs: {component: 'NavBarOverflowMenu', element: 'content'},
        }}
        renderContent={() => (
          <Menu
            className={cx(
              classes.menu,
              isColoredNavbar && classes.coloredMenu,
              isImpersonating && classes.impersonatingMenu
            )}
            dropUp={true}
            height={MenuHeight.LARGE}
            scrollable={true}
            width={MenuWidth.LARGE}
            onClose={() => {
              if (isOverflowMenuOpen) {
                SidepanelActions.closeOverflowMenu();
              }
            }}
          >
            {sections.map((section) => (
              <Option
                key={section.section}
                icon={section.icon}
                iconClassName={css(navBarIconStyles.overflowMenuIcon)}
                isSelected={section.section === activeSection}
                linkClassName={cx(
                  classes.menuItem,
                  isColoredNavbar && classes.coloredMenuItem,
                  isImpersonating && classes.impersonatingMenuItem
                )}
                contentClassName={cx(
                  classes.menuItem,
                  isColoredNavbar && classes.coloredMenuItem,
                  isImpersonating && classes.impersonatingMenuItem
                )}
                onSelect={() => {
                  trackEvent(
                    {
                      module: ANALYTICS_MODULE.NAVIGATION,
                      actionCategory: ACTION_CATEGORY.READ,
                      action: 'clicked_navbar_overflow_menu_option',
                    },
                    {section: section.section}
                  );
                  section.onSectionClick?.(section.section);
                  SidepanelActions.closeOverflowMenu();
                }}
                dataTestAttrs={{component: 'NavBarOverflowMenu', element: 'option', key: section.section}}
              >
                {sectionToReadable(section.section)}
              </Option>
            ))}
          </Menu>
        )}
      >
        <div
          aria-haspopup="menu"
          aria-label="More"
          aria-pressed={isActive}
          className={css(navBarIconStyles.iconWrapper)}
          onClick={() => {
            trackEvent({
              module: ANALYTICS_MODULE.NAVIGATION,
              actionCategory: ACTION_CATEGORY.READ,
              action: isOverflowMenuOpen ? 'closed_navbar_overflow_menu' : 'opened_navbar_overflow_menu',
            });
            if (isOverflowMenuOpen) {
              SidepanelActions.closeOverflowMenu();
            } else {
              SidepanelActions.openOverflowMenu();
            }
          }}
          role="button"
          tabIndex={0}
          {...getTestProps({component: 'NavBarOverflowMenu', element: 'toggle'})}
        >
          <div className={css(navBarIconStyles.inner)}>
            <BenchlingIcon className={css(navBarIconStyles.icon)} icon="ellipsis" />
          </div>
        </div>
      </Float>
    </div>
  );
}
